import { Controller } from "@hotwired/stimulus"

import { getConsumer } from "@shared/lib/action-cable/consumer"

/**
 * Per-ticket presence (Jira-style avatars + "X is typing..." indicator).
 *
 * Subscribes to SupportChannel with { ticket_id } so broadcasts are scoped to
 * this ticket only — no tenant-wide fan-out, no client-side ticket_id filter.
 *
 * Protocol (peer-replicated state, no server-side registry):
 *   presence_join  {user_id, user_name}           server announces a new viewer
 *   presence_hello {user_id, user_name, typing}   peer reply with own state
 *   presence_leave {user_id}                       server announces a viewer left
 *   typing_start   {user_id, user_name}            peer started typing
 *   typing_stop    {user_id}                       peer stopped typing
 *
 * data-controller="support--presence"
 * data-support--presence-ticket-id-value="uuid"
 *
 * Runs on the shared ActionCable consumer (see
 * app/frontend/shared/lib/action-cable/consumer.ts) — so live-updates and
 * presence share one socket. The tab-hide/show cycle below only closes and
 * reopens the SUBSCRIPTION; the shared consumer itself is never disconnected,
 * because that would tear the socket out from under every other controller
 * using it.
 */
const TYPING_THROTTLE_MS = 2000
const TYPING_TRAILING_MS = 4000
const HELLO_COALESCE_MS = 100
const MAX_AVATARS_RENDERED = 5
const PALETTE = [
  "bg-blue-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-violet-500",
  "bg-cyan-500",
  "bg-pink-500",
  "bg-teal-500",
]

export default class extends Controller {
  static targets = ["avatars", "typingLabel"]
  static values = { ticketId: String, spaceGroupId: String }

  connect() {
    this.viewers = new Map() // user_id -> { name, colorIdx }
    this.typists = new Map() // user_id -> { name, trailingTimer }
    this.colorIndex = 0

    this.currentUserId = document.head
      .querySelector('meta[name="current-user-id"]')
      ?.getAttribute("content")

    this.consumer = getConsumer(this.spaceGroupIdValue || "default")
    this._typingThrottleUntil = 0
    this._typingActive = false
    this._helloTimer = null
    this._pendingHello = false

    this._onVisibilityChange = this.#onVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this._onVisibilityChange)

    this.#openSubscription()
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this._onVisibilityChange)
    if (this._helloTimer) {
      clearTimeout(this._helloTimer)
      this._helloTimer = null
    }
    this.#clearTypistTimers()
    this.#closeSubscription()
  }

  // ── Reply-box actions (wired via data-action in the partial) ──────────

  onKeystroke() {
    if (document.hidden) return
    if (!this.subscription) return

    const now = Date.now()
    if (now < this._typingThrottleUntil) return
    this._typingThrottleUntil = now + TYPING_THROTTLE_MS
    this._typingActive = true
    this.subscription.perform("typing_start")
  }

  onReplyBlur() {
    this.#stopTypingLocal()
  }

  onReplySubmit() {
    this.#stopTypingLocal()
  }

  // ── Private ───────────────────────────────────────────────────────────

  #openSubscription() {
    if (this.subscription || !this.ticketIdValue) return
    this.subscription = this.consumer.subscriptions.create(
      { channel: "SupportChannel", ticket_id: this.ticketIdValue },
      { received: (data) => this.#onMessage(data) },
    )
  }

  #closeSubscription() {
    if (!this.subscription) return
    try {
      this.subscription.unsubscribe()
    } catch (_e) {
      // ignore: consumer may already be torn down
    }
    this.subscription = null
    this._typingThrottleUntil = 0
    this._typingActive = false
  }

  #onVisibilityChange() {
    if (document.hidden) {
      this.#stopTypingLocal()
      this.#closeSubscription()
      this.viewers.clear()
      this.#clearTypistTimers()
      this.typists.clear()
      this.#render()
    } else {
      this.#openSubscription()
    }
  }

  #stopTypingLocal() {
    if (!this._typingActive || !this.subscription) {
      this._typingActive = false
      this._typingThrottleUntil = 0
      return
    }
    this._typingActive = false
    this._typingThrottleUntil = 0
    this.subscription.perform("typing_stop")
  }

  #onMessage(data) {
    if (!data || !data.event) return
    // Ignore echoes of our own broadcasts.
    if (data.user_id && data.user_id === this.currentUserId) return

    switch (data.event) {
      case "presence_join":
        this.#onJoin(data)
        break
      case "presence_hello":
        this.#onHello(data)
        break
      case "presence_leave":
        this.#onLeave(data)
        break
      case "typing_start":
        this.#onTypingStart(data)
        break
      case "typing_stop":
        this.#onTypingStop(data)
        break
    }
  }

  #onJoin({ user_id, user_name }) {
    const isNew = !this.viewers.has(user_id)
    this.#upsertViewer(user_id, user_name)
    if (isNew) this.#scheduleHello()
    this.#render()
  }

  #onHello({ user_id, user_name, typing }) {
    this.#upsertViewer(user_id, user_name)
    if (typing) {
      this.#markTyping(user_id, user_name)
    }
    this.#render()
  }

  #onLeave({ user_id }) {
    this.viewers.delete(user_id)
    this.#clearTypist(user_id)
    this.#render()
  }

  #onTypingStart({ user_id, user_name }) {
    this.#upsertViewer(user_id, user_name)
    this.#markTyping(user_id, user_name)
    this.#render()
  }

  #onTypingStop({ user_id }) {
    this.#clearTypist(user_id)
    this.#render()
  }

  #upsertViewer(userId, name) {
    const existing = this.viewers.get(userId)
    this.viewers.set(userId, {
      name: name || existing?.name || "User",
      colorIdx: existing ? existing.colorIdx : this.colorIndex++,
    })
  }

  #markTyping(userId, name) {
    const existing = this.typists.get(userId)
    if (existing?.trailingTimer) clearTimeout(existing.trailingTimer)
    const trailingTimer = setTimeout(() => {
      this.typists.delete(userId)
      this.#render()
    }, TYPING_TRAILING_MS)
    this.typists.set(userId, {
      name: name || existing?.name || this.viewers.get(userId)?.name || "User",
      trailingTimer,
    })
  }

  #clearTypist(userId) {
    const entry = this.typists.get(userId)
    if (entry?.trailingTimer) clearTimeout(entry.trailingTimer)
    this.typists.delete(userId)
  }

  #clearTypistTimers() {
    for (const entry of this.typists.values()) {
      if (entry.trailingTimer) clearTimeout(entry.trailingTimer)
    }
  }

  #scheduleHello() {
    if (!this.subscription) return
    this._pendingHello = true
    if (this._helloTimer) return
    this._helloTimer = setTimeout(() => {
      this._helloTimer = null
      if (this._pendingHello && this.subscription) {
        this._pendingHello = false
        this.subscription.perform("hello")
      }
    }, HELLO_COALESCE_MS)
  }

  #render() {
    this.#renderAvatars()
    this.#renderTypingLabel()
  }

  #renderAvatars() {
    if (!this.hasAvatarsTarget) return
    const entries = Array.from(this.viewers.entries())
    if (entries.length === 0) {
      this.avatarsTarget.replaceChildren()
      this.avatarsTarget.classList.add("hidden")
      return
    }
    this.avatarsTarget.classList.remove("hidden")
    const shown = entries.slice(0, MAX_AVATARS_RENDERED)
    const overflow = entries.length - shown.length
    const nodes = shown.map(([userId, entry]) =>
      this.#buildAvatarNode(userId, entry),
    )
    if (overflow > 0) nodes.push(this.#buildOverflowNode(overflow))
    this.avatarsTarget.replaceChildren(...nodes)
  }

  #renderTypingLabel() {
    if (!this.hasTypingLabelTarget) return
    const target = this.typingLabelTarget
    const typists = Array.from(this.typists.values())
    if (typists.length === 0) {
      target.classList.add("hidden")
      target.textContent = ""
      return
    }
    const names = typists.map((t) => (t.name || "User").split(" ")[0])
    const text =
      names.length === 1
        ? `${names[0]} is typing...`
        : `${names.join(", ")} are typing...`
    if (target.textContent !== text) {
      target.textContent = text
    }
    target.classList.remove("hidden")
  }

  #buildAvatarNode(userId, entry) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative group"
    const typing = this.typists.has(userId)
    const name = entry.name || "User"
    wrapper.title = typing ? `${name} is typing...` : `${name} is viewing`

    const color = PALETTE[entry.colorIdx % PALETTE.length]
    const glow = typing
      ? "ring-2 ring-green-400 animate-pulse"
      : "ring-2 ring-primary/60"

    const circle = document.createElement("div")
    circle.className = `w-7 h-7 rounded-full ${color} ${glow} flex items-center justify-center text-[10px] font-bold text-white shadow-sm cursor-default transition-all`
    circle.textContent = this.#initialsOf(name)
    wrapper.appendChild(circle)

    const dot = document.createElement("span")
    dot.className = `absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ${typing ? "bg-green-400" : "bg-green-500"} border-2 border-background`
    wrapper.appendChild(dot)

    return wrapper
  }

  #buildOverflowNode(count) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative"
    wrapper.title = `${count} more`
    const circle = document.createElement("div")
    circle.className =
      "w-7 h-7 rounded-full bg-muted ring-2 ring-primary/40 flex items-center justify-center text-[10px] font-bold text-muted-foreground shadow-sm cursor-default"
    circle.textContent = `+${count}`
    wrapper.appendChild(circle)
    return wrapper
  }

  #initialsOf(name) {
    return (name || "?")
      .split(" ")
      .map((w) => w[0])
      .filter(Boolean)
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }
}
